"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

export default function FounderSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove("reveal-hidden");
            e.target.classList.add("reveal-active");
          }
        });
      },
      { threshold: 0.08 }
    );
    ref?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-32 md:py-44 px-6 md:px-10 bg-cream" ref={ref}>
      <div className="max-w-8xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          {/* Portrait */}
          <div className="lg:col-span-5 reveal-hidden">
            <div className="relative">
              <div className="rounded-5xl overflow-hidden aspect-[3/4] max-w-sm mx-auto lg:mx-0" style={{ boxShadow: '0 20px 80px rgba(26,61,26,0.2)' }}>
                <AppImage
                  src="/assets/images/Claudiaa-1772742032185.png"
                  alt="Claudia Deichel, fisioterapeuta e fundadora do Studio Pilates Claudia Deichel"
                  fill
                  className="object-cover object-top"
                  priority
                />
              </div>
              {/* Credential badge */}
              <div className="absolute -bottom-4 right-4 md:right-8 glass-card rounded-3xl p-5">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--stone)' }}>Certificada por</p>
                <p className="font-serif font-medium" style={{ color: 'var(--forest)', fontSize: '1rem' }}>BASI Pilates®</p>
                <p className="text-xs mt-1" style={{ color: 'var(--stone)' }}>Formação Internacional</p>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-7">
            <span className="reveal-hidden subheading mb-5 block">Fundadora</span>
            <h2 className="reveal-hidden reveal-delay-1 font-serif text-forest leading-tight mb-10"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
              Claudia Deichel
            </h2>

            <div className="reveal-hidden reveal-delay-2 mb-12" style={{ borderLeft: '2px solid var(--gold)', paddingLeft: '2rem' }}>
              <p className="leading-relaxed mb-5" style={{ color: 'var(--stone)', fontSize: '1.05rem' }}>
                Fisioterapeuta e instrutora de Pilates com mais de 15 anos de experiência, Claudia Deichel construiu sua carreira com dedicação à saúde integral e ao movimento consciente.
              </p>
              <p className="leading-relaxed mb-5" style={{ color: 'var(--stone)', fontSize: '1.05rem' }}>
                Formada em Fisioterapia pela UFRGS e especialista em Pilates pelo método BASI, Claudia desenvolveu uma abordagem única que combina a precisão técnica do Pilates com a sensibilidade do atendimento humanizado.
              </p>
              <p className="leading-relaxed" style={{ color: 'var(--stone)', fontSize: '1.05rem' }}>
                Ao longo de sua trajetória, ela formou centenas de alunos e construiu um studio que hoje é referência na Zona Norte de Porto Alegre.
              </p>
            </div>

            {/* Credentials */}
            <div className="reveal-hidden reveal-delay-3 grid grid-cols-2 gap-4">
              {[
                { icon: "🎓", label: "Fisioterapeuta UFRGS" },
                { icon: "🏅", label: "Certificada BASI Pilates®" },
                { icon: "🌟", label: "15+ anos de experiência" },
                { icon: "👥", label: "1.200+ alunos formados" }
              ]?.map((cred) => (
                <div
                  key={cred?.label}
                  className="flex items-center gap-3 bg-white rounded-2xl p-4"
                  style={{ boxShadow: '0 2px 16px rgba(26,61,26,0.06)', border: '1px solid rgba(232,226,214,0.5)' }}
                >
                  <span className="text-xl">{cred?.icon}</span>
                  <p className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>{cred?.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}